import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useNexusHomeContent } from "@/contexts/NexusHomeContentContext";

const ELIDO_LOGO_FALLBACK =
  "https://customer-assets.emergentagent.com/job_custom-demo-gen/artifacts/x2wfas84_Elido_logotype_blue.png";

// Fallback mirrors the currently published CMS navigation. It is shown only
// after the CMS request succeeds, fails, or times out: never as a temporary menu.
const FALLBACK_NAV = [
  { label: "Hemsidor", href: "/hemsidor" },
  { label: "Google Ads", href: "/google-ads-byra" },
  { label: "Meta Ads", href: "/meta-ads-byra" },
  { label: "SEO", href: "/seo-byra" },
  { label: "AI-chatbotar", href: "/ai-chatbot-hemsida" },
  { label: "Kundcase", href: "/kundcase" },
  { label: "Kontakta oss  →", href: "/#kontakt" },
];

function smoothScrollTo(hashHref) {
  if (!hashHref || hashHref.indexOf("#") === -1) return false;
  const id = hashHref.split("#").pop();
  const el = document.getElementById(id);
  if (!el) return false;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  if (window.history && window.history.replaceState) {
    window.history.replaceState(null, "", hashHref);
  }
  return true;
}

function NavLink({ link, onClick }) {
  // Same-page hash → smooth-scroll. Cross-page (/) prefix → SPA route.
  // Pure hash on a non-Nexus page should still navigate to / first, so we
  // accept "/#anchor" links and let the browser handle them naturally.
  const href = link.href || "#";
  // Contact nav item is rendered as a CTA button so it stands out.
  const isContact =
    typeof link.label === "string" && link.label.toLowerCase().includes("kontakt");

  const className = isContact
    ? "inline-flex items-center justify-center h-10 px-5 rounded-full bg-[#3A8A98] text-white text-sm font-semibold transition-all duration-200 hover:bg-[#4a9aa8] hover:-translate-y-px"
    : "text-sm font-medium text-white/70 hover:text-white transition-colors";

  // Hash-only on the same page (no slash before #)
  const isSamePageHash = href.startsWith("#");
  // Internal SPA route (starts with / but not /#)
  const isInternal = href.startsWith("/") && !href.startsWith("/#");

  if (isSamePageHash) {
    return (
      <a
        href={href}
        className={className}
        onClick={(e) => {
          if (smoothScrollTo(href)) e.preventDefault();
          onClick && onClick();
        }}
        data-testid={`nexus-nav-${link.label}`}
      >
        {link.label}
      </a>
    );
  }
  if (isInternal) {
    return (
      <Link to={href} className={className} onClick={onClick} data-testid={`nexus-nav-${link.label}`}>
        {link.label}
      </Link>
    );
  }
  // /#anchor or external — let the browser handle (full nav for /#anchor)
  return (
    <a href={href} className={className} onClick={onClick} data-testid={`nexus-nav-${link.label}`}>
      {link.label}
    </a>
  );
}

/**
 * Shared site header for public pages. The logo is available immediately,
 * while navigation is revealed once CMS data and its actual font are ready.
 * This prevents both the old fallback menu and serif-to-Inter font flashing
 * during a hard refresh. CMS changes still take effect without redeployment.
 */
export default function NexusHeader() {
  const { content, status } = useNexusHomeContent();
  const cmsSite = content?.site;
  const site = {
    logo_url: cmsSite?.logo_url || ELIDO_LOGO_FALLBACK,
    nav_links: Array.isArray(cmsSite?.nav_links) && cmsSite.nav_links.length > 0
      ? cmsSite.nav_links : FALLBACK_NAV,
  };
  const [open, setOpen] = useState(false);
  const [navFontStatus, setNavFontStatus] = useState("pending");
  const navigationReady = status !== "loading" && navFontStatus !== "pending";
  const navFontFamily = navFontStatus === "loaded"
    ? "'Inter', Arial, sans-serif"
    : "'Segoe UI', Arial, sans-serif";

  // Do not show a fallback font and then swap to Inter after the header paints.
  // If the webfont is unavailable, choose a stable system font for this visit.
  useEffect(() => {
    let active = true;
    let settled = false;
    const finish = (status) => {
      if (!active || settled) return;
      settled = true;
      setNavFontStatus(status);
    };
    const timer = setTimeout(() => finish("fallback"), 3000);
    if (document.fonts && typeof document.fonts.load === "function") {
      Promise.all([
        document.fonts.load("500 14px Inter"),
        document.fonts.load("600 14px Inter"),
      ])
        .then((faces) => finish(faces.every((list) => list.length > 0) ? "loaded" : "fallback"))
        .catch(() => finish("fallback"));
    } else {
      finish("fallback");
    }
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, []);

  return (
    <header
      className="sticky top-0 z-50 bg-[#0a1418]/85 backdrop-blur-md border-b border-white/5"
      data-testid="nexus-header"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center" data-testid="nexus-header-logo">
          <img
            src={site.logo_url || ELIDO_LOGO_FALLBACK}
            alt="Elido"
            className="h-14 w-auto brightness-0 invert"
          />
        </Link>
        <nav
          className="hidden lg:flex items-center gap-8"
          style={{ visibility: navigationReady ? "visible" : "hidden", fontFamily: navFontFamily }}
          aria-hidden={!navigationReady}
        >
          {site.nav_links.map((link, i) => (
            <NavLink key={i} link={link} />
          ))}
        </nav>
        <button
          type="button"
          className="lg:hidden text-white/80 p-2 -mr-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Meny"
          style={{ visibility: navigationReady ? "visible" : "hidden" }}
          disabled={!navigationReady}
          data-testid="nexus-mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {open ? (
        <nav
          className="lg:hidden border-t border-white/5 px-6 py-5 space-y-4 bg-[#0a1418]"
          style={{ fontFamily: navFontFamily }}
        >
          {site.nav_links.map((link, i) => (
            <div key={i} className="block">
              <NavLink link={link} onClick={() => setOpen(false)} />
            </div>
          ))}
        </nav>
      ) : null}
    </header>
  );
}
